import { faCakeCandles, faEnvelope, faExclamationTriangle, faStar, faTriangleExclamation, faUserGear, faUserPlus } from '@fortawesome/pro-regular-svg-icons';
import { useQuery } from '@tanstack/react-query';
import { FC } from 'react';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Chip } from 'src/components/Chip';
import { Icon } from 'src/components/Icon';
import { Link } from 'src/components/Link';
import { Stack } from 'src/components/Stack';
import { Tooltip } from 'src/components/Tooltip';
import { Typography } from 'src/components/Typography';
import { checkAppointmentEligibility } from 'src/models/appointment/appointment';
import {
  fetchClientVisitMilestone,
  fetchUserAlertsWithBirthdate,
} from 'src/models/user/user';
import { getBirthdayMessage } from 'src/models/user/utils';
import { useMessageDialog } from 'src/shared/Dialogs/MessageDialog';
import { Identifier } from 'src/types/utilityTypes';

interface VisitUserInfoProps {
  userName: string;
  userId?: Identifier;
  clientId?: string;
  appointmentId: Identifier;
  visitStatus?: string;
  tagString?: string;
  isNew?: boolean;
  firstVisit?: boolean;
}

/**
 * VisitUserInfo - User info display for visit rows
 *
 * Clean layout:
 * - Row 1: Name + alert icons + action icons
 * - Row 2: Visit count, tags (if any)
 */
export const VisitUserInfo: FC<VisitUserInfoProps> = ({
  userName,
  userId,
  clientId,
  appointmentId,
  visitStatus,
  tagString,
  isNew = false,
  firstVisit = false,
}) => {
  const { openMessageDialog, MessageDialogComponent } = useMessageDialog();

  const { data: milestone } = useQuery({
    queryKey: ['clientVisitMilestone', userId, appointmentId],
    queryFn: () => fetchClientVisitMilestone(userId!, appointmentId),
    enabled: !!userId && !!appointmentId,
  });

  const { data: eligibility, isLoading: isCheckingEligibility } = useQuery({
    queryKey: ['appointmentEligibility', appointmentId, userId],
    queryFn: () =>
      checkAppointmentEligibility({
        appointment: { id: appointmentId } as any,
        user: { id: userId! } as any,
      }),
    enabled: isNew && !!userId && !!appointmentId,
  });

  const { data: alertsData } = useQuery({
    queryKey: ['userAlerts', userId],
    queryFn: () => fetchUserAlertsWithBirthdate(userId!),
    enabled: !!userId,
  });

  const hasAlerts = alertsData?.alerts && alertsData.alerts.length > 0;
  const hasRedAlert = alertsData?.alerts?.some((a) => a.color === 'red');
  const alertMessage = alertsData?.alerts?.map((a) => a.message).join(', ');
  const birthdayMessage = getBirthdayMessage(alertsData?.birthdate ?? null);

  const notCancelled =
    visitStatus !== 'cancelled' &&
    visitStatus !== 'late_cancelled' &&
    visitStatus !== 'noshowed';

  const getMilestoneText = (): string | null => {
    if (!milestone?.visitMilestone) return null;
    if (milestone.visitMilestone === 1 && notCancelled) return 'First visit';
    const count = notCancelled
      ? milestone.visitMilestone
      : milestone.visitMilestone + 1;
    return `${count} visits`;
  };

  const milestoneText = getMilestoneText();
  const showFirstVisitStar =
    firstVisit ||
    (isNew && !milestone?.visitMilestone) ||
    (milestone?.visitMilestone === 1 && notCancelled);

  const hasInsufficientFunds =
    eligibility?.trainerReason === 'insufficient_funds';
  const shouldShowEligibilityWarning =
    isNew && !isCheckingEligibility && eligibility?.reasonMessage;

  return (
    <Box>
      {/* Row 1: Name + Icons */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {/* Name */}
        <Typography
          variant="body2"
          fontWeight="medium"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            minWidth: 0,
          }}
        >
          {userName}
        </Typography>

        {/* Alert/Birthday icons */}
        {hasAlerts && (
          <Tooltip title={alertMessage || 'User has alerts'}>
            <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
              <Icon
                icon={faTriangleExclamation}
                size="xsmall"
                color={hasRedAlert ? 'error' : 'warning'}
              />
            </Box>
          </Tooltip>
        )}
        {birthdayMessage && (
          <Tooltip title={birthdayMessage}>
            <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
              <Icon
                icon={faCakeCandles}
                size="xsmall"
                color="primary"
              />
            </Box>
          </Tooltip>
        )}

        {/* Action icons */}
        {clientId ? (
          <Tooltip title="Manage Client">
            <Link
              href={`/ex4/clients/${clientId}/visits/`}
              target="_blank"
              sx={{ display: 'inline-flex', flexShrink: 0 }}
            >
              <Icon
                icon={faUserGear}
                size="xsmall"
              />
            </Link>
          </Tooltip>
        ) : (
          <Tooltip title="Add as Client">
            <Button
              variant="text"
              size="small"
              sx={{ minWidth: 'auto', p: 0, flexShrink: 0 }}
              onClick={() => {}}
            >
              <Icon
                icon={faUserPlus}
                size="xsmall"
              />
            </Button>
          </Tooltip>
        )}
        {userId && (
          <Tooltip title="Send Message">
            <Button
              variant="text"
              size="small"
              sx={{ minWidth: 'auto', p: 0, flexShrink: 0 }}
              onClick={() => {
                openMessageDialog({
                  recipientId: String(userId),
                  recipientNickname: userName,
                });
              }}
            >
              <Icon
                icon={faEnvelope}
                size="xsmall"
              />
            </Button>
          </Tooltip>
        )}
      </Box>

      {/* Row 2: Metadata (visits, tags, etc) */}
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="baseline"
        sx={{ mt: 0.25 }}
      >
        {isNew && (
          <Chip
            size="small"
            label="NEW"
            color="warning"
            sx={{ height: 18, fontSize: '0.65rem', flexShrink: 0 }}
          />
        )}

        <Typography variant="caption" color="text.secondary">
          {showFirstVisitStar && (
            <Tooltip title="First Visit">
              <Icon
                sx={{ mr: 0.25 }}
                icon={faStar}
                size="tiny"
                color="primary"
              />
            </Tooltip>
          )}

          {milestoneText && `${milestoneText}`}
          {tagString && ` · ${tagString}`}
        </Typography>
      </Stack>

      {/* Eligibility warning (pending visits only) */}
      {isNew && isCheckingEligibility ? (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Checking eligibility...
        </Typography>
      ) : shouldShowEligibilityWarning ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mt: 0.5,
            p: 0.5,
            borderRadius: 0.5,
            bgcolor: hasInsufficientFunds ? 'error.softBg' : 'warning.softBg',
          }}
        >
          <Icon
            icon={faExclamationTriangle}
            size="xsmall"
            color={hasInsufficientFunds ? 'error' : 'warning'}
          />
          <Typography
            variant="caption"
            color={hasInsufficientFunds ? 'error.main' : 'warning.dark'}
          >
            {eligibility?.reasonMessage}
          </Typography>
        </Box>
      ) : null}

      <MessageDialogComponent />
    </Box>
  );
};
